import { random } from 'lodash';
import type { NodeExecuteTaskParams } from '$/domains/node/type';
import type { RoleGenFormValues, RoleGenMaterialFilter } from './RoleSettingForm';
import type { RoleGenModelOption, RoleGenModelParameterValue } from './useModelOptions';

export const ROLE_GEN_BUSINESS_TYPE = 'gen_role';
export const ROLE_GEN_COST_ITEM_ID = 'create-role';

/** 组装与画布任务一致的创建角色埋点快照，工具类型使用 gen_role。 */
export function buildRoleGenExecuteExtra() {
  return {
    snapshot: {
      group_id: '',
      group_type: 'normal',
      tool_type: ROLE_GEN_BUSINESS_TYPE,
      node_type: 'IMAGE_NODE',
      node_texts: [],
      input_texts: [],
      material_list: '',
      asset_list: '',
    },
  } as const;
}

type RoleGenMaterialExtra = {
  id?: string;
  name?: string;
  cover_url?: string;
  filter?: {
    genre_id: number;
    tags: Record<string, string[]>;
    keyword?: string;
  };
};

export type RoleGenNodeExtra = {
  role_gen: {
    name: string;
    prompt: string;
    reference_image_url?: string;
    materials: {
      clothing: RoleGenMaterialExtra;
      hairstyle: RoleGenMaterialExtra;
      accessory: RoleGenMaterialExtra;
    };
    model: {
      model_id: string;
      node_model_id: string;
      api_name: string;
      parameters: Record<string, RoleGenModelParameterValue>;
    };
  };
};

const ROLE_GEN_PROMPT_PARAMETER_KEY = 'prompt';
const ROLE_GEN_IMAGE_LIST_PARAMETER_KEY = 'image_list';

type RoleGenTaskParameterValue = RoleGenModelParameterValue | string[];
type RoleGenTaskParameters = Record<string, RoleGenTaskParameterValue>;

/** 将单类参考素材转换为创建角色历史回显需要的 node_extra 结构。 */
function buildRoleGenMaterialExtra(
  id?: string,
  name?: string,
  coverUrl?: string,
  filter?: RoleGenMaterialFilter,
): RoleGenMaterialExtra {
  return {
    id,
    name,
    cover_url: coverUrl,
    filter: filter
      ? {
        genre_id: filter.genreId,
        tags: filter.tags,
        keyword: filter.keyword,
      }
      : undefined,
  };
}

/** 将角色表单转换为可持久化的 role_gen 快照，供任务回显和资产重新编辑共用。 */
export function buildRoleGenRoleSnapshot(values: RoleGenFormValues): RoleGenNodeExtra['role_gen'] {
  return {
    name: values.name,
    prompt: values.prompt,
    reference_image_url: values.referenceImageUrl,
    materials: {
      clothing: buildRoleGenMaterialExtra(
        values.clothingMaterialId,
        values.clothingMaterialName,
        values.clothingMaterialCoverUrl,
        values.clothingMaterialFilter,
      ),
      hairstyle: buildRoleGenMaterialExtra(
        values.hairstyleMaterialId,
        values.hairstyleMaterialName,
        values.hairstyleMaterialCoverUrl,
        values.hairstyleMaterialFilter,
      ),
      accessory: buildRoleGenMaterialExtra(
        values.accessoryMaterialId,
        values.accessoryMaterialName,
        values.accessoryMaterialCoverUrl,
        values.accessoryMaterialFilter,
      ),
    },
    model: {
      model_id: values.modelId,
      node_model_id: values.nodeModelId,
      api_name: values.modelApiName,
      parameters: values.modelParameters,
    },
  };
}

/** 收集角色生成服务端需要消费的所有原始图片资源。 */
function buildRoleGenImageList(values: RoleGenFormValues): string[] {
  return [
    values.referenceImageUrl,
    values.clothingMaterialCoverUrl,
    values.hairstyleMaterialCoverUrl,
    values.accessoryMaterialCoverUrl,
  ].reduce<string[]>((mediaList, url) => {
    const mediaUrl = url?.trim();
    return mediaUrl ? [...mediaList, mediaUrl] : mediaList;
  }, []);
}

/** 根据 character 配置组装真实模型参数，排除服务端专用的角色素材数组。 */
function buildRoleGenModelParameters(
  values: RoleGenFormValues,
  model: RoleGenModelOption,
): Record<string, RoleGenModelParameterValue> {
  const configuredKeySet = new Set(Object.keys(model.parameterConfigs));
  const parameters: Record<string, RoleGenModelParameterValue> = {};
  Object.entries({
    ...model.parameters,
    ...values.modelParameters,
  }).forEach(([key, value]) => {
    if (configuredKeySet.has(key)) {
      parameters[key] = value;
    }
  });

  if (configuredKeySet.has(ROLE_GEN_PROMPT_PARAMETER_KEY)) {
    parameters[ROLE_GEN_PROMPT_PARAMETER_KEY] = values.prompt.trim();
  }

  return parameters;
}

/** 根据 character 配置组装 countcost 与 nodeexecute 共用的角色生成参数，输出比例固定为 16:9。 */
export function buildRoleGenTaskParameters(
  values: RoleGenFormValues,
  model: RoleGenModelOption,
): RoleGenTaskParameters {
  return {
    ...buildRoleGenModelParameters(values, model),
    [ROLE_GEN_IMAGE_LIST_PARAMETER_KEY]: buildRoleGenImageList(values),
    ratio: '16:9',
  };
}

/** 组装 nodeexecute 节点级 node_extra，仅保留历史回显需要的数据。 */
export function buildRoleGenNodeExtra(
  values: RoleGenFormValues,
  model: RoleGenModelOption,
  parameters: Record<string, RoleGenModelParameterValue>,
): RoleGenNodeExtra {
  const roleGenSnapshot = buildRoleGenRoleSnapshot(values);
  return {
    role_gen: {
      ...roleGenSnapshot,
      name: roleGenSnapshot.name.trim(),
      prompt: roleGenSnapshot.prompt.trim(),
      model: {
        model_id: model.modelId,
        node_model_id: model.nodeModelId,
        api_name: model.apiName,
        parameters,
      },
    },
  };
}

/** 生成角色专用 nodeexecute 任务体，保持 parameters 与询价参数同源。 */
export function buildRoleGenNodeExecuteTask(
  values: RoleGenFormValues,
  model: RoleGenModelOption,
  nodeId: string,
  nodeExtraValues: RoleGenFormValues = values,
): NodeExecuteTaskParams {
  const parameters = buildRoleGenTaskParameters(values, model);
  return {
    name: model.apiName,
    tool_abstract_name: { cn: '生成角色', en: '' },
    node_id: nodeId,
    node_extra: buildRoleGenNodeExtra(nodeExtraValues, model, buildRoleGenModelParameters(values, model)),
    parameters: {
      ...parameters,
      random: `${Date.now()}-${random(10000000, 99999999)}`,
    },
  };
}
